/*  iceberg-ui  */
import { Icon } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    CheckBoxRounded,
    LooksOne,
    LooksOneOutlined,
    LooksTwo,
    LooksTwoOutlined
} from '@mui/icons-material';
import { Alert, Badge, Box, Button, Grid, IconButton } from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import Steps from 'application/Shared/Components/Drawer/Steps';
import { MultiInput } from 'application/Shared/Components/Inputs';
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import {
    FontSizes,
    NoteTypesKeys,
    NoteTypesValues
} from 'application/Shared/Constants';
import {
    Helpers,
    Notify,
    Payload,
    Transition
} from 'application/Shared/Helpers';

/*  Components  */
import {
    Modal,
    ModalBody,
    ModalFooter
} from 'application/Shared/Modals/Components';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/* Objects */
import { Application } from 'objects/Application';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

import { ClientIcon } from './Components/ClientIcon';
import Community from './Components/Community';
import { CommunityIcon } from './Components/CommunityIcon';
import { PartnerIcon } from './Components/PartnerIcon';

/*  Functions  */
const { isEmpty } = Helpers;

/*  Constants  */
const endpoints = {
    client: '/note',
    community: '/communityNote',
    completed: '/completedNote',
    imported: '/importedNote',
    partner: '/partnerNote'
};

/*  Templates  */
const tmpls = {
    client: {
        all: false,
        disabled: false,
        icon: <ClientIcon sx={{ color: 'icon.primary' }} />,
        message: '',
        type: NoteTypesValues[0]
    },
    community: {
        actionDisabled: true,
        all: true,
        checkAction: true,
        community: true,
        disabled: true,
        icon: <CommunityIcon sx={{ color: 'icon.primary' }} />
    },
    partner: {
        all: true,
        disabled: false,
        icon: <PartnerIcon sx={{ color: 'icon.primary' }} />,
        message: '',
        type: NoteTypesValues[0]
    }
};

const NotesIcon = ({ icon, total, unimported }) => (
    <Badge
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        badgeContent={total || Helpers.count(unimported)}
        color="secondary"
        overlap="rectangular"
        sx={{
            // Has to match anchorOrigin above
            '& .MuiBadge-anchorOriginBottomRight': {
                bottom: '5px',
                right: '5px'
            }
        }}
    >
        {icon}
    </Badge>
);

/*  State  */
const state = () => ({
    added: {},
    addedNotes: [],
    completed: [],
    fields: [],
    importing: [],
    importingNote: false,
    loadPublish: false,
    modified: {},
    notes: [],
    open: false,
    partnerNoteIds: [],
    publishFields: [],
    publishNotes: [],
    removed: {},
    /* drawer steps */
    steps: [
        {
            active: true,
            icons: {
                active: (
                    <LooksOne
                        sx={{ color: 'icon.primary', fontSize: '75px' }}
                    />
                ),
                inactive: (
                    <LooksOneOutlined
                        sx={{ color: 'icon.disabled', fontSize: '75px' }}
                    />
                )
            },
            num: 1,
            text: 'Create'
        },
        {
            active: false,
            icons: {
                active: (
                    <LooksTwo
                        sx={{ color: 'icon.primary', fontSize: '75px' }}
                    />
                ),
                inactive: (
                    <LooksTwoOutlined
                        sx={{ color: 'icon.disabled', fontSize: '75px' }}
                    />
                )
            },
            num: 2,
            text: 'Save & Publish'
        }
    ],
    total: 0,
    unimported: [],
    unimporting: [],
    view: 'main'
});

const hasNotes = (v) => {
    if (!v) {
        return false;
    }

    const notes = v.filter((n) => n.type && n.message);
    if (!notes || !notes.length) {
        return false;
    }

    return true;
};

const saveRemoved = async (params) => {
    const { notes, endpoint } = params;
    if (isEmpty(notes)) {
        return true;
    }

    const ids = [];
    notes.forEach((n) => {
        ids.push(n.id);
    });

    debug.dev('Deleting Notes', ids, endpoint);
    try {
        await axios.delete(`${endpoint}/bulk`, {
            params: { id: Payload.idString(ids) }
        });
    } catch (e) {
        debug.e('Unable to Delete Notes', e, ids, endpoint);
        return false;
    }

    return true;
};

class AppNotes extends Component {
    constructor(props) {
        super(props);
        this.state = state();
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prev) {
        const { reload, open } = this.props;
        if (reload) {
            this.reload();
            return;
        }

        if (!prev.open && open) {
            this.reload();
        }
    }

    componentWillUnmount() {
        this.unload();
    }

    load = () => {
        const { partnerHub, modalOnly, open } = this.props;

        if (modalOnly) {
            this.setState({ open });
            if (!open) {
                return;
            }
        }

        let { clientNotes, partnerNotes, communityNotes } = this.props;

        if (isEmpty(communityNotes)) {
            communityNotes = [];
        }

        if (partnerHub && isEmpty(partnerNotes)) {
            partnerNotes = [{ ...tmpls.partner, new: true }];
        }
        if (!Array.isArray(partnerNotes)) {
            partnerNotes = [];
        }

        if (!partnerHub && isEmpty(clientNotes)) {
            clientNotes = [{ ...tmpls.client, new: true }];
        }
        if (!Array.isArray(clientNotes)) {
            clientNotes = [];
        }

        this.makeFields();
        this.makeNotes({ clientNotes, communityNotes, partnerNotes });
    };

    reload = () => {
        this.setState(state(), () => this.load());
    };

    unload = () => {
        this.setState(state());
    };

    makeFields = () => {
        const { partner, partnerHub, t } = this.props;

        const type = {
            default: t(`Notes.${NoteTypesValues[0]}`),
            field: 'type',
            items: NoteTypesKeys, // TODO: Localize these, somehow make it work with multiinput
            label: 'Type',
            tooltip: 'Notes.type',
            type: 'select'
        };

        const message = {
            field: 'message',
            label: 'Note / Recommendation',
            multiline: true,
            placeholder: 'Note / Recommendation'
        };

        const icon = {
            default: <ClientIcon sx={{ color: 'icon.primary' }} />,
            field: 'icon',
            hidden: true
        };

        const fields = [type, message];
        const publishFields = [
            { ...type, disabled: true },
            { ...message, disabled: true }
        ];

        fields.push(icon);

        if (partner) {
            const all = {
                afterActions: true,
                default: false,
                disabled: false,
                field: 'all',
                label: 'Publish To All Clients?',
                type: 'checkbox'
            };

            if (partnerHub) {
                icon.default = <PartnerIcon sx={{ color: 'icon.primary' }} />;
                all.default = true;
                all.disabled = true;
            }

            fields.push(all);
            publishFields.push(icon);
        }

        publishFields.push({
            afterActions: true,
            default: false,
            disabled: false,
            field: 'publish',
            label: 'Publish to Community?',
            type: 'checkbox'
        });

        this.setState({ fields, publishFields });
    };

    makeNotes = (params) => {
        const { partnerHub } = this.props;
        const { clientNotes, partnerNotes, communityNotes } = params;

        const notes = [];
        const unimported = [];
        const partnerNoteIds = [];
        let total = 0;

        // Community Notes
        communityNotes.forEach((n) => {
            const { imported, id, type, message, dateCreated } = n;
            const note = { ...tmpls.community, dateCreated, id, message, type };
            if (partnerHub) {
                note.checkAction = false;
                note.actionDisabled = false;
            } else {
                note.actionDisabled = false;
            }

            if (!imported) {
                unimported.push(note);
            } else {
                notes.push(note);
                total += 1;
            }
        });

        // Partner Notes
        partnerNotes.forEach((n) => {
            const { id, type, message } = n;
            const note = { ...tmpls.partner, id, message, type };
            if (!partnerHub) {
                note.disabled = true;
                if (!n.new) {
                    note.checkAction = true;
                }
            }
            if (n.new) {
                note.new = true;
            } else {
                total += 1;
            }

            notes.push(note);
            partnerNoteIds.push(id);
        });

        // Client Notes
        clientNotes.forEach((n) => {
            const { id, type, message } = n;
            const note = { ...tmpls.client, id, message, type };
            if (n.new) {
                note.new = true;
            } else {
                note.checkAction = true;
                total += 1;
            }
            notes.push(note);
        });

        this.setState({ notes, partnerNoteIds, total, unimported });
    };

    saveImporting = async () => {
        const { application, match } = this.props;
        const { clientId } = match.params;
        const { importing } = this.state;
        if (isEmpty(importing)) {
            return true;
        }

        const payload = [];
        importing.forEach((n) => {
            const params = { applicationId: application.id, noteId: n.id };
            if (clientId) {
                params.platformTenantId = clientId;
            }
            payload.push(params);
        });

        debug.dev('Importing Community Notes', importing);
        try {
            await axios.post(`${endpoints.imported}/bulk`, payload);
        } catch (e) {
            debug.e('Unable to Import Community Notes', e, payload);
            return false;
        }

        return true;
    };

    saveUnimporting = async () => {
        const { unimporting } = this.state;
        if (isEmpty(unimporting)) {
            return true;
        }

        const ids = [];
        unimporting.forEach((n) => {
            ids.push(n.id);
        });

        debug.dev('Unimporting Community Notes', ids);
        try {
            await axios.delete(`${endpoints.imported}/bulk`, {
                params: { noteId: Payload.idString(ids) }
            });
        } catch (e) {
            debug.e('Unable to Unimport Community Notes', e, ids);
            return false;
        }

        return true;
    };

    saveCompleted = async () => {
        const { match } = this.props;
        const { clientId } = match.params;
        const { completed } = this.state;

        if (isEmpty(completed)) {
            return true;
        }

        const payload = [];
        completed.forEach((n) => {
            let type = 'partner';
            if (n.community) {
                type = 'community';
            }
            if (n.client) {
                type = 'client';
            }
            const params = { noteId: n.id, type };
            if (clientId) {
                params.platformTenantId = clientId;
            }
            payload.push(params);
        });

        debug.dev('Saving Completed Notes', completed);
        try {
            await axios.post(`${endpoints.completed}/bulk`, payload);
        } catch (e) {
            debug.e('Unable to Save Completed Notes', e, payload);
            return false;
        }

        return true;
    };

    saveAdded = async (p) => {
        const { application, match } = this.props;
        const { clientId } = match.params;
        const { notes, endpoint } = p;
        if (!hasNotes(notes)) {
            return true;
        }

        const applicationId = application.id;
        const payload = [];
        notes.forEach((note) => {
            const { type, message } = note;
            const params = { applicationId, message, type };
            if (clientId) {
                params.platformTenantId = clientId;
            }
            payload.push(params);
        });

        debug.dev('Saving New Notes', payload, endpoint);

        try {
            await axios.post(`${endpoint}/bulk`, payload);
        } catch (e) {
            debug.e('Unable to Save New Notes', e, notes, endpoint);
            return false;
        }

        return true;
    };

    saveModified = async (p) => {
        const { match } = this.props;
        const { clientId } = match.params;
        const { notes, endpoint } = p;
        if (!hasNotes(notes)) {
            return true;
        }

        const payload = [];
        notes.forEach((note) => {
            const { id, type, message } = note;
            if (!message) {
                return;
            }
            const params = { id, message, type };
            if (clientId) {
                params.platformTenantId = clientId;
            }
            payload.push(params);
        });

        debug.dev('Saving Modified Notes', payload, endpoint);
        try {
            await axios.put(`${endpoint}/bulk`, payload);
        } catch (e) {
            debug.e('Unable to Update Notes', e, payload, endpoint);
            return false;
        }

        return true;
    };

    showPublish = (addedList) => {
        let { steps } = this.state;
        const addedNotes = addedList.map((n) => ({ ...n, publish: false }));
        if (!hasNotes(addedNotes)) {
            this.handleClose({ reload: true });
        }

        steps = steps.map((s) => {
            const { num, icons } = s;
            if (num === 1) {
                return {
                    ...s,
                    active: false,
                    icons: {
                        ...icons,
                        inactive: (
                            <CheckBoxRounded
                                sx={{
                                    color: 'checkbox.checked.secondary',
                                    fontSize: '75px'
                                }}
                            />
                        )
                    }
                };
            }
            return { ...s, active: true };
        });

        this.setState(
            { addedNotes, loadPublish: true, steps, view: 'publish' },
            () => {
                this.setState({ loadPublish: false });
            }
        );
    };

    savePartner = async () => {
        const { partnerNoteIds } = this.state;
        const { added, modified, removed } = this.state;
        const endpoint = endpoints.partner;

        const partnerAdded = Object.values(added).filter((n) => n.all);
        const partnerModified = Object.values(modified).filter((n) => n.all);
        const partnerRemoved = Object.values(removed).filter((n) => n.all);

        // If 'Push To All Clients' was checked, the note turned into a 'Modified Partner Note'
        // But, it should be an 'Added Partner Note'
        // We also need to set to to be removed as a 'Client Note'
        partnerModified.forEach((n, index) => {
            const { id } = n;
            if (partnerNoteIds.includes(id)) {
                return;
            }

            partnerAdded.push(n);
            partnerModified.splice(index, 1);
            removed[id] = { ...n, all: false };
        });

        this.setState({ removed });

        let success = false;
        success = await this.saveAdded({ endpoint, notes: partnerAdded });
        success = await this.saveModified({
            endpoint,
            notes: partnerModified
        });
        success = await saveRemoved({
            endpoint,
            notes: partnerRemoved
        });

        return success;
    };

    saveClient = async () => {
        const { added, modified, removed } = this.state;
        const endpoint = endpoints.client;

        // Added Notes
        const clientAdded = Object.values(added).filter((n) => !n.all);
        let success = await this.saveAdded({ endpoint, notes: clientAdded });

        // Modified Notes
        const clientModified = Object.values(modified).filter((n) => !n.all);
        success = await this.saveModified({ endpoint, notes: clientModified });

        // Removed Notes
        const clientRemoved = Object.values(removed).filter((n) => !n.all);
        success = await saveRemoved({ endpoint, notes: clientRemoved });

        return success;
    };

    handleSave = async () => {
        const { partner } = this.props;
        const { notes, added } = this.state;

        let success = await this.saveImporting();
        success = await this.saveUnimporting();
        success = await this.saveCompleted();
        success = await this.savePartner();
        success = await this.saveClient();

        if (!success) {
            Notify.e('Unable to save notes. Please try again later.');
            this.handleClose({ reload: false });
            return;
        }

        Notify.s('Successfully saved notes.');
        this.setState({ notes });

        const addedList = Object.values(added);
        if (!isEmpty(addedList) && partner) {
            this.showPublish(addedList);
            return;
        }

        this.handleClose({ reload: true });
    };

    handlePublish = async () => {
        const { publishNotes } = this.state;

        if (!publishNotes.length) {
            this.handleClose({ reload: true });
            return;
        }

        const success = await this.saveAdded({
            endpoint: endpoints.community,
            notes: publishNotes
        });

        if (!success) {
            Notify.e('Unable to publish notes. Please try again later.');
            this.handleClose({ reload: false });
            return;
        }

        this.handleClose({ reload: true });
    };

    handleClose = ({ reload }) => {
        const { onSave, onClose } = this.props;

        this.unload();
        if (onSave) {
            onSave(reload);
        }

        if (!reload && onClose) {
            onClose();
        }
    };

    handleCompleted = (params) => {
        const { completed, removed } = this.state;
        const { row } = params;
        const { all } = row;
        if (all) {
            completed.push(row);
        } else {
            completed.push({ ...row, client: true });
        }

        debug.dev('Completed / Removed', completed, removed);
        this.setState({ completed, removed });
    };

    handleChange = (params) => {
        const { unimporting } = this.state;
        const { row, rows, added, modified, removed, remove } = params;
        const { community } = row;

        if (remove && community) {
            unimporting.push(row);
        }

        this.setState({ added, modified, notes: rows, removed });
    };

    handleChangePublish = (params) => {
        const { rows } = params;
        const publishNotes = [];

        rows.forEach((n) => {
            const { type, message, publish } = n;

            if (publish) {
                publishNotes.push({ message, type });
            }
        });

        this.setState({ publishNotes });
    };

    handleImporting = (params) => {
        const { notes, importing } = this.state;
        const { row } = params;
        const note = { ...row, ...tmpls.community };

        importing.push(note);

        this.setState({ importing, importingNote: note, notes }, () => {
            this.setState({ importingNote: false });
        });
    };

    renderPublish = () => {
        const { open, publishFields, addedNotes, loadPublish } = this.state;

        return (
            <MultiInput
                fields={publishFields}
                max={30}
                min={0}
                noActions
                onChange={(p) => this.handleChangePublish(p)}
                open={open}
                reload={loadPublish}
                showIcons
                values={addedNotes}
            />
        );
    };

    renderNoteInput = () => {
        const { open, fields, notes, importingNote } = this.state;

        return (
            <MultiInput
                actionsTooltip="Notes.input_actions"
                fields={fields}
                importingRow={importingNote}
                max={30}
                min={0}
                onChange={(p) => this.handleChange(p)}
                onCheck={(p) => this.handleCompleted(p)}
                onReload={() => this.reload()}
                open={open}
                showIcons
                values={notes}
                zeroText="Add Note"
            />
        );
    };

    renderView = () => {
        const { view } = this.state;

        if (view === 'publish') {
            return this.renderPublish();
        }

        return this.renderNoteInput();
    };

    renderContent = () => {
        const { partner } = this.props;
        const { open, view, notes, addedNotes, steps } = this.state;
        const length = view === 'main' ? notes.length : addedNotes.length;
        const paddingTop = length * 1.5;

        if (!partner) {
            return (
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        {this.renderView()}
                    </Grid>
                </Grid>
            );
        }

        return (
            <Grid container spacing={0}>
                <Grid item xs={1}>
                    <Steps open={open} steps={steps} iconSx={{ paddingTop }} />
                </Grid>
                <Grid item xs={11} sx={{ paddingLeft: '24px' }}>
                    {this.renderView()}
                </Grid>
            </Grid>
        );
    };

    renderActions = () => {
        const { t } = this.props;
        const { view, publishNotes } = this.state;

        if (view === 'main') {
            return (
                <>
                    <Button
                        data-trackid="notesModalCancelBtn"
                        onClick={() => this.handleClose({ reload: false })}
                        variant="outlined"
                        color="primary"
                    >
                        {t('Common.cancel')}
                    </Button>
                    <Button
                        data-trackid="notesModalSaveBtn"
                        onClick={() => this.handleSave()}
                        color="primary"
                        variant="contained"
                    >
                        {t('Common.save')}
                    </Button>
                </>
            );
        }

        return (
            <Box>
                <Alert severity="info" icon={false}>
                    {t('Notes.publish_notice')}
                </Alert>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    pt="16px"
                    width="100%"
                >
                    <Button
                        data-trackid="notesModalDoNotPublishBtn"
                        onClick={() => this.handleClose({ reload: true })}
                        variant="outlined"
                        color="primary"
                    >
                        {t('Common.no_thanks')}
                    </Button>
                    <Button
                        data-trackid="notesModalPublishBtn"
                        onClick={() => this.handlePublish()}
                        color="primary"
                        variant="contained"
                        disabled={!publishNotes.length}
                        sx={{ marginLeft: '8px' }}
                    >
                        {t('Common.publish')}
                    </Button>
                </Box>
            </Box>
        );
    };

    renderCommunity = () => {
        const { application, partner } = this.props;
        const { open, unimported } = this.state;

        if (!partner || !open) {
            return null;
        }

        return (
            <Community
                open={open}
                notes={unimported}
                application={application}
                onImport={(p) => this.handleImporting(p)}
            />
        );
    };

    renderModal = () => {
        const { application, partner } = this.props;
        const { open } = this.state;

        if (!open) {
            return null;
        }

        return (
            <Modal
                open={open}
                aria-labelledby="simple-dialog-title"
                onClose={() => {
                    this.handleClose({ reload: false });
                }}
                PaperProps={{
                    sx: {
                        left: '30px',
                        m: 0,
                        maxWidth: '70%',
                        minWidth: '70%',
                        p: 0,
                        position: 'fixed',
                        top: '100px'
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                alignCenter={false}
                title={
                    <Grid container spacing={1}>
                        {partner && <Grid item xs={1} />}
                        <Grid
                            item
                            xs={partner ? 11 : 12}
                            sx={partner ? { paddingLeft: '50px' } : {}}
                        >
                            <Application data={application}>
                                <ApplicationNameLinkCell
                                    categoryNoTruncate
                                    nameClassname="bold"
                                    nameNoTruncate
                                    useWhiteFont
                                    withCategory
                                />
                            </Application>
                        </Grid>
                    </Grid>
                }
            >
                <ModalBody
                    dividers
                    sx={{ maxHeight: '60vh', paddingBottom: '50px' }}
                >
                    {this.renderContent()}
                    {this.renderCommunity()}
                </ModalBody>
                <ModalFooter>
                    <Grid container>
                        <Grid item xs={1} />
                        <Grid
                            item
                            xs={11}
                            sx={{ paddingLeft: '24px' }}
                            display="flex"
                            justifyContent="space-between"
                        >
                            {this.renderActions()}
                        </Grid>
                    </Grid>
                </ModalFooter>
            </Modal>
        );
    };

    renderButton = () => {
        const { t } = this.props;
        const { unimported, total } = this.state;

        const communityAvail = Helpers.count(unimported);
        const tooltip = (
            <>
                <Box>
                    {total} {t('Common.notes_and_recommendations')}
                </Box>
                <Box>{communityAvail} Community Notes Available</Box>
            </>
        );

        if (!total) {
            if (communityAvail) {
                return (
                    <DarkTooltip
                        title={tooltip}
                        data-trackid="notesRenderButtonTooltipCommunityNoTotal"
                    >
                        <Box>
                            <NotesIcon
                                icon={
                                    <IconButton
                                        sx={{ color: 'notes.icon.open' }}
                                        data-trackid="notesRenderButtonTooltipCommunityNoTotalButton"
                                        onClick={() =>
                                            this.setState({ open: true })
                                        }
                                    >
                                        <Icon size={FontSizes[2]} src="Group" />
                                    </IconButton>
                                }
                                total={total}
                                unimported={unimported}
                            />
                        </Box>
                    </DarkTooltip>
                );
            }

            return (
                <DarkTooltip
                    title={tooltip}
                    data-trackid="notesRenderButtonTooltipNoTotal"
                >
                    <Box>
                        <NotesIcon
                            icon={
                                <IconButton
                                    sx={{ color: 'notes.icon.open' }}
                                    data-trackid="notesRenderButtonTooltipAddNote"
                                    onClick={() =>
                                        this.setState({ open: true })
                                    }
                                >
                                    <Icon size={FontSizes[2]} src="AddNote" />
                                </IconButton>
                            }
                            total={total}
                            unimported={unimported}
                        />
                    </Box>
                </DarkTooltip>
            );
        }

        return (
            <DarkTooltip
                title={tooltip}
                data-trackid="notesRenderButtonTooltip"
            >
                <Box data-trackid="notesRenderButtonButton">
                    <NotesIcon
                        icon={
                            <IconButton
                                sx={{ color: 'notes.icon.open' }}
                                data-trackid="notesRenderButtonButton"
                                onClick={() => this.setState({ open: true })}
                            >
                                <Icon size={FontSizes[2]} src="TaskList" />
                            </IconButton>
                        }
                        total={total}
                        unimported={unimported}
                    />
                </Box>
            </DarkTooltip>
        );
    };

    render() {
        const { modalOnly } = this.props;

        if (modalOnly) {
            return this.renderModal();
        }

        return (
            <>
                {this.renderButton()}
                {this.renderModal()}
            </>
        );
    }
}

export default withRouter(withTranslation()(AppNotes));
